import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthErrorScreen, AuthLoadingScreen } from '@/auth/AuthErrorScreen'
import { useAuth } from '@/auth/AuthContext'
import { checkEmailVerification, sendVerificationEmail } from '@/auth/firebase'
import { consumeAuthRedirectPath } from '@/auth/storage'
import { useTranslation } from '@/i18n'

const RESEND_COOLDOWN_SECONDS = 60
const SPAM_HINT_DELAY_MS = 20_000
const POLL_INTERVAL_MS = 5_000

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const { authState, loading, refreshAuthState, signOut, user } = useAuth()
  const { t } = useTranslation()
  const [sending, setSending] = useState(false)
  const [checking, setChecking] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [error, setError] = useState('')
  const [lastSentAt, setLastSentAt] = useState<number | null>(null)
  const [now, setNow] = useState(Date.now())
  const [showElevatedSpamHint, setShowElevatedSpamHint] = useState(false)
  const continueTimerRef = useRef<number | null>(null)

  const email = user?.email?.trim() || ''
  const secondsUntilResend = useMemo(() => {
    if (lastSentAt == null) return 0
    const elapsedSeconds = Math.floor((now - lastSentAt) / 1000)
    return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsedSeconds)
  }, [lastSentAt, now])

  useEffect(() => {
    if (loading) return

    if (authState === 'ready') {
      navigate(consumeAuthRedirectPath('/dashboard'), { replace: true })
      return
    }

    if (authState === 'needsProfile') {
      navigate('/account-aanmaken', { replace: true })
      return
    }

    if (authState === 'notLoggedIn') {
      navigate('/login', { replace: true })
    }
  }, [authState, loading, navigate])

  useEffect(() => {
    if (authState !== 'verifyEmail') return

    const spamHintTimer = window.setTimeout(() => {
      setShowElevatedSpamHint(true)
    }, SPAM_HINT_DELAY_MS)

    const interval = window.setInterval(() => {
      void handleCheck(false)
    }, POLL_INTERVAL_MS)

    return () => {
      window.clearTimeout(spamHintTimer)
      window.clearInterval(interval)
    }
  }, [authState])

  useEffect(() => {
    if (lastSentAt == null || secondsUntilResend <= 0) return

    const timer = window.setInterval(() => {
      setNow(Date.now())
    }, 1000)

    return () => window.clearInterval(timer)
  }, [lastSentAt, secondsUntilResend])

  useEffect(() => {
    return () => {
      if (continueTimerRef.current != null) {
        window.clearTimeout(continueTimerRef.current)
      }
    }
  }, [])

  async function handleCheck(showFeedback = true) {
    setChecking(true)
    setError('')

    try {
      const verified = await checkEmailVerification()
      if (verified) {
        setEmailVerified(true)
        setFeedback(t('E-mailadres geverifieerd. We zetten je door...'))
        continueTimerRef.current = window.setTimeout(() => {
          void refreshAuthState()
        }, 300)
        return
      }

      if (showFeedback) {
        setFeedback(t('Nog niet geverifieerd. Check ook je spamfolder.'))
      }
    } catch (checkError) {
      const message =
        checkError instanceof Error
          ? checkError.message
          : t('We konden de verificatiestatus niet ophalen.')
      setError(message)
    } finally {
      setChecking(false)
    }
  }

  async function handleResend() {
    if (secondsUntilResend > 0) {
        setError(t('Even wachten voordat je opnieuw kunt sturen ({seconds}s).', { seconds: secondsUntilResend }))
      return
    }

    setError('')
    setFeedback('')
    setSending(true)

    try {
      await sendVerificationEmail()
      setLastSentAt(Date.now())
      setNow(Date.now())
      setFeedback(t('Verificatie-e-mail verzonden.'))
    } catch (sendError) {
      const message =
        sendError instanceof Error
          ? sendError.message
          : t('Verificatie-e-mail versturen is mislukt.')
      setError(message)
    } finally {
      setSending(false)
    }
  }

  if (loading) return <AuthLoadingScreen fullscreen={false} />
  if (authState === 'error') return <AuthErrorScreen fullscreen={false} />
  if (authState !== 'verifyEmail') return <AuthLoadingScreen fullscreen={false} />

  return (
    <div className="mx-auto w-full max-w-md text-center">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-surface-700 bg-surface-800/80">
        <span className="text-4xl" aria-hidden="true">
          {emailVerified ? '✓' : '✉'}
        </span>
      </div>

      <h2 className="mt-6 text-2xl font-semibold text-white">
        {emailVerified ? t('E-mailadres geverifieerd') : t('Verifieer je e-mailadres')}
      </h2>
      <p className="mt-3 text-sm leading-6 text-surface-400">
        {emailVerified
          ? t('Je kunt nu verder naar de app.')
          : t('We hebben een verificatie-e-mail gestuurd naar:')}
      </p>
      <p className="mt-2 text-base font-semibold text-surface-100">{email || t('Onbekend e-mailadres')}</p>

      {!emailVerified ? (
        <div className="mt-5 rounded-2xl border border-surface-800 bg-surface-800/60 px-4 py-3 text-left text-sm text-surface-300">
          <p>
            {showElevatedSpamHint
              ? t('Nog niets ontvangen? Check ook je spamfolder. Verificatiemails belanden daar soms.')
              : t('Zie je de e-mail niet meteen? Kijk dan ook even in je spamfolder.')}
          </p>
        </div>
      ) : null}

      {feedback ? (
        <p className="mt-4 rounded-lg bg-accent-950/40 px-4 py-2.5 text-sm text-accent-200">
          {feedback}
        </p>
      ) : null}

      {error ? (
        <p className="mt-4 rounded-lg bg-red-950/60 px-4 py-2.5 text-sm text-red-400">{error}</p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => void handleCheck(true)}
          disabled={checking}
          className="rounded-xl bg-accent-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {checking ? t('Controleren...') : t('Ik heb geverifieerd')}
        </button>
        <button
          type="button"
          onClick={() => void handleResend()}
          disabled={sending || secondsUntilResend > 0}
          className="rounded-xl border border-surface-700 px-4 py-3 text-sm font-semibold text-surface-200 transition hover:border-surface-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {sending
            ? t('Verzenden...')
            : secondsUntilResend > 0
              ? t('Opnieuw sturen over {seconds}s', { seconds: secondsUntilResend })
              : t('Verstuur e-mail opnieuw')}
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="rounded-xl px-4 py-3 text-sm font-medium text-surface-400 transition hover:text-white"
        >
          {t('Terug naar login')}
        </button>
      </div>
    </div>
  )
}
